import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, RefreshCw, CheckCircle2, AlertCircle, LogOut } from 'lucide-react';
import { auth, sendEmailVerification, signOut } from '@/firebase';
import { Button } from '@/components/ui/Button';
import { AppAction } from '@/types';

/**
 * 🔒 VerifyEmail — blocking gate
 * Shown once a signed-up-with-email-and-password account passes 48 hours without verifying.
 * (Google sign-up already verifies the email intrinsically, so those accounts never land here.)
 * App.tsx redirects here via `needsEmailVerification` in its route-gating logic.
 */
export const VerifyEmail: React.FC<{ dispatch: React.Dispatch<AppAction> }> = ({ dispatch }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async () => {
    if (!auth.currentUser) return;
    setResending(true);
    setError(null);
    try {
      await sendEmailVerification(auth.currentUser);
      setResent(true);
    } catch {
      setError(t('verify_email.err_resend', 'Could not send the email right now. Please try again shortly.'));
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerified = async () => {
    if (!auth.currentUser) return;
    setChecking(true);
    setError(null);
    try {
      // reload() is the only way to pick up a verification done in another tab/device —
      // onAuthStateChanged does not re-fire just because emailVerified changed server-side.
      await auth.currentUser.reload();
      dispatch({ type: 'SET_EMAIL_VERIFIED', payload: auth.currentUser.emailVerified });
      if (auth.currentUser.emailVerified) {
        navigate('/dashboard', { replace: true });
      } else {
        setError(t('verify_email.err_not_yet', "Still not verified — check your inbox and click the link, then try again."));
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 p-6">
      <div className="w-full max-w-md">
        <div className="p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl text-center space-y-6">
          <div className="inline-flex p-5 rounded-[2rem] bg-brand/10 text-brand mx-auto">
            <Mail className="w-10 h-10" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase italic">
              {t('verify_email.title', 'Verify Your Email')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 leading-relaxed">
              {t(
                'verify_email.body',
                "It's been more than 48 hours since you signed up and your email ({{email}}) still isn't verified. Check your inbox for the verification link to keep using EverydayOS.",
                { email: auth.currentUser?.email ?? '' }
              )}
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-left text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-xs font-bold">{error}</span>
            </div>
          )}
          {resent && !error && (
            <div className="flex items-center gap-2 text-left text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 rounded-xl p-3">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="text-xs font-bold">{t('verify_email.resent', 'Verification email sent — check your inbox.')}</span>
            </div>
          )}

          <div className="space-y-3">
            <Button onClick={handleCheckVerified} isLoading={checking} className="w-full rounded-2xl h-12 font-black uppercase text-xs tracking-widest">
              <CheckCircle2 className="w-4 h-4 mr-2" /> {t('verify_email.check', "I've Verified — Continue")}
            </Button>
            <Button variant="outline" onClick={handleResend} isLoading={resending} className="w-full rounded-2xl h-12 font-black uppercase text-xs tracking-widest">
              <RefreshCw className="w-4 h-4 mr-2" /> {t('verify_email.resend', 'Resend Verification Email')}
            </Button>
            <button
              onClick={() => signOut(auth)}
              className="w-full flex items-center justify-center gap-2 text-2xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 pt-2"
            >
              <LogOut className="w-3 h-3" /> {t('verify_email.sign_out', 'Sign Out')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
